import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Car, 
  CheckCircle2, 
  Calculator,
  ArrowRight,
  Phone,
  ChevronDown,
  MapPin,
  Bike,
  Info,
  Users,
  Award,
  Clock,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useFAQSchema, commonFAQs } from '@/utils/aiSeoSchemas';
import usePageContent from '@/hooks/usePageContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FuehrerscheinPage = () => {
  const { t: c } = usePageContent('fuehrerschein');
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [licenseClasses, setLicenseClasses] = useState([]);
  const [allLicenseClasses, setAllLicenseClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [unitValues, setUnitValues] = useState({});
  const [loadingPricing, setLoadingPricing] = useState(true);

  // AI-optimized FAQ Schema
  useFAQSchema(commonFAQs.general);

  // Static info for display (icons, colors, descriptions)
  const licenseClassesInfo = {
    'B': { name: 'Klasse B', desc: 'PKW bis 3,5t', icon: Car, minAge: 17, color: 'blue' },
    'BE': { name: 'Klasse BE', desc: 'PKW mit Anhänger', icon: Car, minAge: 18, color: 'indigo' },
    'A': { name: 'Klasse A', desc: 'Motorrad unbegrenzt', icon: Bike, minAge: 24, color: 'red' },
    'A2': { name: 'Klasse A2', desc: 'Motorrad bis 35kW', icon: Bike, minAge: 18, color: 'orange' },
    'A1': { name: 'Klasse A1', desc: 'Leichtkraftrad 125ccm', icon: Bike, minAge: 16, color: 'amber' },
    'AM': { name: 'Klasse AM', desc: 'Roller & Moped', icon: Bike, minAge: 15, color: 'green' },
  };

  // Get unique class types that are available across all locations, sorted by their order in DB
  const availableClassTypes = useMemo(() => {
    // Group by class_type and get the minimum order for each
    const classOrderMap = {};
    allLicenseClasses.forEach(lc => {
      const order = lc.order ?? 999;
      if (!(lc.class_type in classOrderMap) || order < classOrderMap[lc.class_type]) {
        classOrderMap[lc.class_type] = order;
      }
    });
    
    const uniqueTypes = Object.keys(classOrderMap);
    // Sort by the order from DB
    return uniqueTypes.sort((a, b) => classOrderMap[a] - classOrderMap[b]);
  }, [allLicenseClasses]);

  useEffect(() => {
    document.title = 'Führerschein Kosten & Preise | Alle Klassen | Fahrschule Rößner Frankfurt';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Führerschein Kosten berechnen für alle Klassen: B, BE, A, A1, A2, AM. Transparente Preise in Frankfurt, Offenbach & Bad Homburg. Fahrschule Rößner - 34 Jahre Erfahrung.');
    }
    
    // Course Schema for AI optimization
    const courseSchema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Führerschein Ausbildung",
      "description": "Komplette Führerschein Ausbildung für alle Klassen: PKW, Motorrad, Roller. Moderne Fahrzeuge, erfahrene Fahrlehrer, über 95% Bestehensquote.",
      "provider": {
        "@type": "DrivingSchool",
        "name": "Fahrschule Rößner",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Frankfurt am Main",
          "addressRegion": "Hessen",
          "addressCountry": "DE"
        }
      },
      "educationalLevel": "Beginner",
      "teaches": ["Fahren", "Verkehrsregeln", "Sicheres Fahren"],
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "courseMode": "onsite",
        "instructor": {
          "@type": "Person",
          "name": "Erfahrene Fahrlehrer"
        }
      }
    };

    const script = document.createElement('script');
    script.id = 'course-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(courseSchema);
    document.head.appendChild(script);

    fetchLocations();
    fetchAllLicenseClasses();
    
    return () => {
      const script = document.getElementById('course-schema');
      if (script) script.remove();
    };
  }, []);

  const fetchAllLicenseClasses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/license-classes`);
      setAllLicenseClasses(response.data.filter(lc => lc.active !== false));
    } catch (error) {
      console.error('Error fetching all license classes:', error);
    }
  };

  useEffect(() => {
    if (selectedLocation) {
      fetchLicenseClasses(selectedLocation);
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (selectedClass) {
      const initialUnits = {};
      selectedClass.cost_components?.forEach(comp => {
        if (comp.is_per_unit) {
          initialUnits[comp.name] = comp.default_units;
        }
      });
      setUnitValues(initialUnits);
    }
  }, [selectedClass]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations`);
      setLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0].slug);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoadingPricing(false);
    }
  };

  const fetchLicenseClasses = async (locationSlug) => {
    setLoadingPricing(true);
    try {
      const response = await axios.get(`${API_URL}/api/license-classes/location/${locationSlug}`);
      setLicenseClasses(response.data);
      const klasseB = response.data.find(c => c.class_type === 'B');
      if (klasseB) {
        setSelectedClass(klasseB);
      } else if (response.data.length > 0) {
        setSelectedClass(response.data[0]);
      } else {
        setSelectedClass(null);
      }
    } catch (error) {
      console.error('Error fetching license classes:', error);
      setLicenseClasses([]);
      setSelectedClass(null);
    } finally {
      setLoadingPricing(false);
    }
  };

  const handleLocationChange = (slug) => {
    setSelectedLocation(slug);
    setSelectedClass(null);
    setUnitValues({});
  };

  const handleClassChange = (classId) => {
    const selected = licenseClasses.find(c => c.id === classId);
    setSelectedClass(selected);
  };

  const handleUnitChange = (componentName, value) => {
    setUnitValues(prev => ({
      ...prev,
      [componentName]: value
    }));
  };

  const getClassIcon = (classType) => {
    if (['A', 'A1', 'A2', 'AM'].includes(classType)) {
      return <Bike className="w-5 h-5" />;
    }
    return <Car className="w-5 h-5" />;
  };

  const gesamtkosten = selectedClass?.cost_components?.reduce((sum, comp) => {
    if (comp.is_per_unit) {
      const units = unitValues[comp.name] ?? comp.default_units;
      return sum + (comp.price * units);
    }
    return sum + comp.price;
  }, 0) || 0;

  const faqs = [
    {
      question: c('faq1_q', 'Was kostet ein Führerschein insgesamt?'),
      answer: c('faq1_a', 'Die Kosten variieren je nach Führerscheinklasse. Klasse B (PKW) liegt durchschnittlich bei 2.500-3.500 €, Motorradklassen bei 1.500-2.500 €, und Klasse AM bei 800-1.200 €.')
    },
    {
      question: c('faq2_q', 'Welche Führerscheinklassen bietet ihr an?'),
      answer: c('faq2_a', 'Wir bieten alle gängigen Führerscheinklassen an: Klasse B und BE (PKW), Klasse A, A2, A1 (Motorrad) sowie Klasse AM (Roller/Moped).')
    },
    {
      question: c('faq3_q', 'Wie lange dauert die Führerscheinausbildung?'),
      answer: c('faq3_a', 'Die Dauer variiert je nach Klasse und persönlichem Lernfortschritt. Klasse B dauert durchschnittlich 3-4 Monate, Motorradklassen 2-3 Monate.')
    },
    {
      question: c('faq4_q', 'Kann ich mehrere Führerscheinklassen gleichzeitig machen?'),
      answer: c('faq4_a', 'Ja, das ist möglich und oft sogar günstiger. Bei der Kombination von Klassen können Theoriestunden zusammengelegt werden.')
    },
    {
      question: c('faq5_q', 'Ab welchem Alter kann ich mit dem Führerschein beginnen?'),
      answer: c('faq5_a', 'Das Mindestalter variiert: Klasse AM ab 15 Jahren, A1 ab 16 Jahren, B (BF17) ab 17 Jahren, A2 ab 18 Jahren.')
    },
    {
      question: c('faq6_q', 'Bietet ihr Intensivkurse an?'),
      answer: c('faq6_a', 'Ja! Für alle Führerscheinklassen bieten wir Intensivkurse an. Kontaktiere uns für einen individuellen Plan.')
    },
  ];

  return (
    <div className="pt-20" data-testid="fuehrerschein-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="absolute inset-0 bg-grid opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
              <Calculator className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Führerschein Kostenrechner</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {c('hero_title', 'Führerschein Kosten berechnen – Alle Klassen')}
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              {c('hero_subtitle', 'Berechne die Kosten für deinen Führerschein: PKW, Motorrad oder Roller. Transparente Preise für alle Führerscheinklassen an unseren Standorten.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D">
                <Button 
                  type="submit"
                  size="lg" 
                  className="bg-blue-600 text-white hover:bg-blue-700 rounded-full px-8"
                  data-testid="fuehrerschein-cta"
                >
                  Jetzt anmelden <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
              <a href="tel:06920326059">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8"
                >
                  <Phone className="mr-2 w-5 h-5" /> Kostenlose Beratung
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* License Classes Overview - Only show classes that exist in the database */}
      {availableClassTypes.length > 0 && (
        <section className="py-16 bg-slate-50" data-testid="license-classes-overview">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
              Unsere Führerscheinklassen
            </h2>
            <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Von PKW über Motorrad bis Roller – wir bilden dich in allen gängigen Führerscheinklassen aus.
            </p>
            <div className={`grid gap-4 ${
              availableClassTypes.length <= 3 
                ? 'grid-cols-1 sm:grid-cols-3 max-w-2xl mx-auto' 
                : availableClassTypes.length <= 4 
                  ? 'grid-cols-2 md:grid-cols-4 max-w-4xl mx-auto' 
                  : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
            }`}>
              {availableClassTypes.map((classType) => {
                const info = licenseClassesInfo[classType];
                if (!info) return null;
                const IconComponent = info.icon;
                const colorClasses = {
                  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
                  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
                  red: { bg: 'bg-red-100', text: 'text-red-600' },
                  orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
                  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
                  green: { bg: 'bg-green-100', text: 'text-green-600' },
                };
                const colors = colorClasses[info.color] || colorClasses.blue;
                
                return (
                  <Card 
                    key={classType} 
                    className="text-center hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-blue-500"
                    onClick={() => {
                      const classInList = licenseClasses.find(c => c.class_type === classType);
                      if (classInList) {
                        setSelectedClass(classInList);
                        document.getElementById('kostenrechner')?.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    <CardContent className="p-4">
                      <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3 ${colors.bg}`}>
                        <IconComponent className={`w-6 h-6 ${colors.text}`} />
                      </div>
                      <h3 className="font-bold text-slate-900">{info.name}</h3>
                      <p className="text-sm text-slate-500">{info.desc}</p>
                      <p className="text-xs text-slate-400 mt-1">ab {info.minAge} Jahren</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Award className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">{c('stat1_title', 'Erfahrene Fahrlehrer')}</h3>
              <p className="text-sm text-slate-600">{c('stat1_desc', 'Über 10.000 erfolgreiche Fahrschüler')}</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">{c('stat2_title', '95% Bestehensquote')}</h3>
              <p className="text-sm text-slate-600">{c('stat2_desc', 'Überdurchschnittlich hohe Erfolgsrate')}</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">{c('stat3_title', '4 Standorte')}</h3>
              <p className="text-sm text-slate-600">{c('stat3_desc', 'Frankfurt, Offenbach & Bad Homburg')}</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">{c('stat4_title', 'Faire Preise')}</h3>
              <p className="text-sm text-slate-600">{c('stat4_desc', 'Transparent & ohne versteckte Kosten')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Calculator */}
      <section id="kostenrechner" className="py-16 bg-slate-50" data-testid="cost-calculator-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              {c('rechner_title', 'Führerschein Kostenrechner')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {c('rechner_subtitle', 'Berechne die voraussichtlichen Kosten für deinen Führerschein. Wähle deinen Standort und die gewünschte Führerscheinklasse.')}
            </p>
          </div>

          <Card className="max-w-4xl mx-auto shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-xl">
                <Calculator className="w-6 h-6" />
                Kostenrechner
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 md:p-8">
              {/* Location & Class Selector */}
              <div className="mb-6 p-4 bg-blue-50 rounded-xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      Standort auswählen
                    </Label>
                    <Select value={selectedLocation} onValueChange={handleLocationChange}>
                      <SelectTrigger className="w-full mt-1 bg-white" data-testid="location-selector">
                        <SelectValue placeholder="Standort auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.slug} value={loc.slug}>
                            {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      {selectedClass && ['A', 'A1', 'A2', 'AM'].includes(selectedClass.class_type) 
                        ? <Bike className="w-4 h-4 text-blue-600" />
                        : <Car className="w-4 h-4 text-blue-600" />
                      }
                      Führerscheinklasse
                    </Label>
                    <Select 
                      value={selectedClass?.id || ''} 
                      onValueChange={handleClassChange}
                      disabled={licenseClasses.length === 0}
                    >
                      <SelectTrigger className="w-full mt-1 bg-white" data-testid="class-selector">
                        <SelectValue placeholder={licenseClasses.length === 0 ? "Keine Klassen verfügbar" : "Klasse wählen..."} />
                      </SelectTrigger>
                      <SelectContent>
                        {licenseClasses.map((lc) => (
                          <SelectItem key={lc.id} value={lc.id}>
                            <div className="flex items-center gap-2">
                              {getClassIcon(lc.class_type)}
                              <span>{lc.class_name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3">Die Preise können je nach Standort variieren.</p>
              </div>

              {/* Class Info */}
              {selectedClass && (
                <div className="mb-6 p-4 bg-slate-50 rounded-xl flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    ['A', 'A1', 'A2', 'AM'].includes(selectedClass.class_type) ? 'bg-red-100' : 'bg-blue-100'
                  }`}>
                    {getClassIcon(selectedClass.class_type)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{selectedClass.class_name}</p>
                    <p className="text-sm text-slate-600">{selectedClass.description}</p>
                    {selectedClass.min_age && (
                      <p className="text-sm text-slate-500 mt-1">Mindestalter: {selectedClass.min_age} Jahre</p>
                    )}
                  </div>
                </div>
              )}

              {loadingPricing ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : selectedClass?.cost_components ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Calculator Inputs */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-blue-600" />
                      Deine Angaben
                    </h3>
                    <div className="space-y-6">
                      {selectedClass.cost_components.filter(c => c.is_per_unit).map((comp) => (
                        <div key={comp.name}>
                          <Label htmlFor={comp.name}>{comp.label}</Label>
                          <div className="flex items-center gap-4 mt-2">
                            <div className="flex-1">
                              <Slider
                                value={[unitValues[comp.name] ?? comp.default_units]}
                                onValueChange={(value) => handleUnitChange(comp.name, value[0])}
                                min={comp.min_units}
                                max={comp.max_units}
                                step={1}
                                className="w-full"
                              />
                            </div>
                            <Input
                              id={comp.name}
                              type="number"
                              min={comp.min_units}
                              max={comp.max_units}
                              value={unitValues[comp.name] ?? comp.default_units}
                              onChange={(e) => handleUnitChange(comp.name, parseInt(e.target.value) || comp.min_units)}
                              className="w-20 text-center"
                              data-testid={`calculator-${comp.name}`}
                            />
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            {comp.min_units > 0 ? `Mindestens ${comp.min_units} Pflicht` : `Durchschnitt: ${comp.default_units}`}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cost Breakdown */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Kostenaufstellung</h3>
                    <div className="space-y-3">
                      {selectedClass.cost_components.map((comp) => (
                        <div key={comp.name} className="flex justify-between items-center py-2 border-b border-slate-100">
                          <span className="text-slate-600">
                            {comp.is_per_unit 
                              ? `${unitValues[comp.name] ?? comp.default_units} × ${comp.label} à ${comp.price.toFixed(2)} €`
                              : comp.label
                            }
                          </span>
                          <span className="font-medium">
                            {comp.is_per_unit 
                              ? ((unitValues[comp.name] ?? comp.default_units) * comp.price).toFixed(2)
                              : comp.price.toFixed(2)
                            } €
                          </span>
                        </div>
                      ))}
                      <div className="pt-4 border-t-2 border-slate-200">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-slate-900">Geschätzte Gesamtkosten</span>
                          <span className="text-2xl font-bold text-blue-600" data-testid="total-cost">
                            ca. {gesamtkosten.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : licenseClasses.length === 0 && !loadingPricing ? (
                <div className="text-center py-8">
                  <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Keine Klassen verfügbar</h3>
                  <p className="text-slate-500">
                    Für diesen Standort sind derzeit keine Führerscheinklassen eingerichtet.
                  </p>
                </div>
              ) : null}

              <p className="text-xs text-slate-500 text-center mt-6">
                * Die tatsächlichen Kosten können je nach individuellem Lernfortschritt und Anzahl der benötigten Fahrstunden variieren.
              </p>

              {/* CTA Buttons */}
              {selectedClass && (
                <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t">
                  <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D" className="flex-1">
                    <Button 
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      Jetzt unverbindlich anmelden <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </form>
                  <a href="tel:06920326059" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <Phone className="mr-2 w-4 h-4" /> 069 20 32 60 59
                    </Button>
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Häufig gestellte Fragen
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`} className="bg-white rounded-lg border px-6">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-blue-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Bereit für deinen Führerschein?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Melde dich jetzt an und starte deine Ausbildung bei der Fahrschule Rößner!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D">
              <Button 
                type="submit"
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8"
              >
                Jetzt anmelden <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>
            <Link to="/kontakt">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-8"
              >
                Kostenlose Beratung
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FuehrerscheinPage;
